import React, { useEffect, useMemo, useState } from 'react';
import {
  BOOT_VARIANTS,
  BOOT_KEYFRAMES,
  BOOT_DURATION_MS,
  pickRandomVariant,
} from '../lib/bootVariants';

/**
 * BootSplash — launch splash (Feb-2026 rewrite, user brief).
 *
 * Requirements from user:
 *   • Instead of a white screen + logo, show one of the 20 randomized
 *     loading-screen variants we designed in /boot-lab-v2.
 *   • Every picture must appear INSTANTLY — no lazy fades, no browser
 *     "?" broken-image icon, no empty tiles waiting on a network hop.
 *   • Icon pops in on top after the mosaic is already visible.
 *
 * Implementation notes:
 *   • Tiles are `<div>` with `background-image` (NOT `<img>`). A failed
 *     background-image simply doesn't paint — the browser cannot render
 *     the "?" broken-image glyph on a `<div>`. Rule 20's "no question
 *     marks over undownloaded images" is thus impossible to violate.
 *   • The speaker cover pool is cached to `sessionStorage` on the first
 *     successful fetch. On EVERY subsequent launch (this tab or a new
 *     one within the same session) the mosaic paints on the very first
 *     React render, before any network request completes.
 *   • The variant (cols / anim / dim / logo) is picked randomly per
 *     mount via `pickRandomVariant()` — every launch gets a different
 *     splash from the 20.
 *   • Splash always plays for the full BOOT_DURATION_MS timeline. The
 *     old once-per-session flag was dropped — user wants the mosaic on
 *     every launch, not only the very first.
 */

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = '/vaad-otzar-logo.jpg';
const GOLD = '#c9a437';
// Feb-2026 v6 (user "i see the vaad otzar blue logo as every square
// before turns into speaker — want speakers to be clear pics and
// load faster"): the LOGO_TILE_URL backdrop under each tile was
// causing a visible "logo → portrait" flip on every tile. Removed
// entirely; the mosaic now hard-gates on `tilesReady` and paints all
// portraits at once (see decode-all effect below). Bumped cache key
// from v5 → v6 so any client holding the old logo-layered tiles
// refetches on next launch.
const POOL_CACHE_KEY = 'vaad_boot_pool_cache_v6';
// Feb-2026 v6.2 (testing agent RCA "warm cache is not actually warm
// because seed=Math.random() re-shuffles fids every mount"): persist
// the last SELECTED 16-fid set separately so the SW IMG_CACHE from a
// previous launch actually hits on the next launch. We store it
// after selection and read it synchronously as initial state.
const MOSAIC_FIDS_KEY = 'vaad_boot_mosaic_fids_v1';
const FADE_MS = 500;
// Feb-2026 v6.4 (user "theres should be no duplicate pics on loading
// screen"): every grid cell must show a UNIQUE speaker. We take
// `needed` (= cols × rows, typically 40-60) unique fids from the
// pool, capped so we never ask for more than the pool has. The set
// is persisted so subsequent launches reuse the same URLs and the
// SW IMG_CACHE (warmed on the previous launch via postMessage) hits.
// Cold-cache bytes are 40-60 × 11 KB ≈ 500-660 KB which still lands
// inside the 3.2 s splash on 4G; warm-cache mounts in <200 ms.
const MAX_UNIQUE_TILES_HARD_CAP = 80;
// How long we'll wait for portraits to decode before painting. On
// cold cache we reveal PROGRESSIVELY: once >= READY_THRESHOLD of
// unique tiles are decoded OR DECODE_TIMEOUT_MS elapses (whichever
// comes first) we flip `tilesReady` true.
const DECODE_TIMEOUT_MS = 1600;
const READY_THRESHOLD = 0.6;

const shuffleSeeded = (arr, seed) => {
  const a = arr.slice();
  let s = seed;
  for (let i = a.length - 1; i > 0; i -= 1) {
    s = (s * 9301 + 49297) % 233280;
    const j = Math.floor((s / 233280) * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// Feb-2026 (user "make them load a lot faster, dont need to be perfect
// quality"): switched from `/api/portrait/by-file-id?original=1` which
// returns the full-res 100-500 KB image (up to 20 MB per splash across
// 40 tiles) to `/api/gallery/photo/{fid}?size=200` — a Pillow-resized
// JPEG that comes in at ~11 KB per tile. At 40 tiles the whole mosaic
// is <500 KB total and paints inside the 1.5 s preload window on
// cellular, killing the "blue background under tiles" state.
const fidUrl = (fid) => `${API}/api/gallery/photo/${fid}?size=200`;

/** Read cached pool from localStorage synchronously — used as the
 *  initial state so the mosaic can paint on the FIRST render without
 *  waiting for the /api/boot-mosaic-pool fetch to resolve. Persists
 *  across sessions so every launch after the first paints instantly. */
const readCachedPool = () => {
  try {
    const raw = localStorage.getItem(POOL_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) return parsed;
  } catch { /* ignore */ }
  // Feb-2026 (user "half the time i see a dark blue square"): fall
  // back to sessionStorage in case localStorage is unavailable (iOS
  // Safari private mode, ITP-cleared, wrapped WebView). Better than
  // waiting on the API when we've already fetched the pool this tab.
  try {
    const raw = sessionStorage.getItem(POOL_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) return parsed;
  } catch { /* ignore */ }
  return [];
};

const writeCachedPool = (fids) => {
  try {
    localStorage.setItem(POOL_CACHE_KEY, JSON.stringify(fids));
  } catch { /* quota / private mode — ignore */ }
  try {
    sessionStorage.setItem(POOL_CACHE_KEY, JSON.stringify(fids));
  } catch { /* quota / private mode — ignore */ }
};

/** Read the last-selected 16-fid mosaic set. Kept STABLE across
 *  launches so the SW IMG_CACHE hits. */
const readCachedMosaicFids = () => {
  try {
    const raw = localStorage.getItem(MOSAIC_FIDS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) return parsed;
  } catch { /* ignore */ }
  return [];
};

const writeCachedMosaicFids = (fids) => {
  try { localStorage.setItem(MOSAIC_FIDS_KEY, JSON.stringify(fids)); }
  catch { /* ignore */ }
};

const BootSplash = () => {
  const [visible, setVisible] = useState(true);
  const [fadingOut, setFadingOut] = useState(false);
  // Initial pool state pulled synchronously from sessionStorage so we
  // can paint tiles on the first frame.
  const [pool, setPool] = useState(readCachedPool);
  // Feb-2026 v6: mosaic is hidden until ALL tile portraits have
  // decoded (or the decode-timeout elapses). This kills the "logo
  // behind every square, then flip to portrait" flicker the previous
  // build had. Users now see: navy → full mosaic of clear portraits,
  // no logo underlay ever.
  const [tilesReady, setTilesReady] = useState(false);
  const [vp] = useState({
    w: typeof window !== 'undefined' ? window.innerWidth : 1200,
    h: typeof window !== 'undefined' ? window.innerHeight : 800,
  });

  // Random variant per mount — every launch shows a different splash.
  const variant = useMemo(
    () => pickRandomVariant() || BOOT_VARIANTS[0],
    [],
  );

  // Fresh random seed per mount so the tile ORDER inside the variant is
  // also different each session. `variant.seed` from bootVariants is
  // just a stable spacer for the lab — for the live splash we want
  // fresh randomness on every boot.
  const seed = useMemo(() => Math.floor(Math.random() * 1000000), []);

  // Fetch (or refresh) the pool. If we already have a cached pool the
  // mosaic is already painting — the fetch just refreshes it silently.
  // Cache is written UNCONDITIONALLY (even if the component unmounted
  // before the fetch resolves) so a very short first-boot still
  // populates localStorage for the next launch.
  //
  // Feb-2026 (user "half the time i see a dark blue square"): if the
  // in-memory pool is empty on mount, race the bundled
  // `/boot-pool.json` (SW-shell-precached → 0-network) against the
  // live API. Whichever comes back first paints tiles — usually the
  // bundled snapshot resolves in <5 ms so the mosaic is real
  // portraits on the very first frame, not logo-wallpaper.
  useEffect(() => {
    if (!visible) return undefined;
    let alive = true;

    // Race 1: bundled snapshot (always available, 0-network via SW).
    if (pool.length === 0) {
      fetch('/boot-pool.json')
        .then((r) => r.ok ? r.json() : null)
        .then((data) => {
          const fids = Array.isArray(data?.fids) ? data.fids.filter(Boolean) : [];
          if (alive && fids.length > 0 && pool.length === 0) {
            setPool(fids);
            // Don't overwrite cache from the snapshot — live API wins.
          }
        })
        .catch(() => { /* silent */ });
    }

    // Race 2: live API (authoritative — updates cache when it lands).
    fetch(`${API}/api/boot-mosaic-pool`)
      .then((r) => r.json())
      .then((data) => {
        const fids = Array.isArray(data?.fids) ? data.fids.filter(Boolean) : [];
        if (fids.length > 0) {
          writeCachedPool(fids);
          if (alive) setPool(fids);
        }
      })
      .catch(() => { /* silent — bundled/cached pool wins */ });
    return () => { alive = false; };
  }, [visible]);

  // Autoclose timeline.
  useEffect(() => {
    if (!visible) return undefined;
    const fadeTimer = setTimeout(() => setFadingOut(true), BOOT_DURATION_MS - FADE_MS);
    const doneTimer = setTimeout(() => setVisible(false), BOOT_DURATION_MS);
    return () => { clearTimeout(fadeTimer); clearTimeout(doneTimer); };
  }, [visible]);

  // Feb-2026 (user "half the time i see a dark blue square, want
  // images loaded instantly"): drop cols cap from 8 → 5 on mobile
  // (≤600 px wide) so the grid is ≈40 tiles instead of ≈150. That
  // slashes the boot-mosaic total download from ~1.5 MB → ~400 KB and
  // lets every real portrait land inside the 3.2 s splash window on
  // cellular. Desktop still gets 8 cols for that "wall of faces"
  // feel.
  const BOOT_MAX_COLS = vp.w < 600 ? 5 : 8;

  // Feb-2026 v6.4 (user "theres should be no duplicate pics on
  // loading screen"): every grid cell gets a UNIQUE fid — no
  // repetition. If the pool has fewer fids than the grid needs we
  // simply render fewer tiles (empty cells stay dark navy, but no
  // face ever appears twice). The selected fid SET is persisted so
  // the next launch reuses the same URLs and the SW IMG_CACHE hits.
  const { cols, rows, tiles, uniqueTiles } = useMemo(() => {
    const rawCols = variant.cols || 8;
    const c = Math.min(rawCols, BOOT_MAX_COLS);
    const tileW = vp.w / c;
    const r = Math.max(3, Math.ceil(vp.h / tileW) + 1);
    const needed = c * r;
    if (pool.length === 0) return { cols: c, rows: r, tiles: [], uniqueTiles: [] };
    const wanted = Math.min(needed, pool.length, MAX_UNIQUE_TILES_HARD_CAP);
    // Prefer the last-persisted set IF it's still large enough AND
    // every fid is still in the current pool. Otherwise pick a new
    // stable slice.
    const persisted = readCachedMosaicFids();
    const poolSet = new Set(pool);
    let unique;
    if (
      persisted.length >= wanted &&
      persisted.slice(0, wanted).every((f) => poolSet.has(f))
    ) {
      unique = persisted.slice(0, wanted);
    } else {
      const shuffled = shuffleSeeded(pool, seed);
      unique = shuffled.slice(0, wanted);
      writeCachedMosaicFids(unique);
    }
    // Shuffle the placement order per-mount so the same fid set
    // renders in different positions each launch (visual variety
    // without breaking cache stability).
    const laidOut = shuffleSeeded(unique, seed + 7);
    return { cols: c, rows: r, tiles: laidOut, uniqueTiles: unique };
  }, [pool, variant.cols, vp, seed, BOOT_MAX_COLS]);

  // Feb-2026 v6.1 (testing agent: "reveal on hard 900 ms timer paints
  // an empty grid on cold cache"): PROGRESSIVE reveal. As each unique
  // tile decodes we bump `readyCount`. Once >= READY_THRESHOLD of
  // unique tiles are ready OR DECODE_TIMEOUT_MS elapses (whichever
  // comes first) we flip `tilesReady` true.
  //
  // Stability guard: once `tilesReady` is true we IGNORE further pool
  // updates so the mosaic can't blink back to opacity 0 when the live
  // API result overwrites the bundled snapshot mid-splash.
  useEffect(() => {
    if (uniqueTiles.length === 0) return undefined;
    let cancelled = false;
    let readyCount = 0;
    const threshold = Math.max(1, Math.ceil(uniqueTiles.length * READY_THRESHOLD));
    const check = () => {
      if (cancelled) return;
      if (readyCount >= threshold) setTilesReady(true);
    };
    uniqueTiles.forEach((fid) => {
      const img = new Image();
      img.decoding = 'async';
      try { img.fetchPriority = 'high'; } catch { /* older engines */ }
      img.src = fidUrl(fid);
      img.decode()
        .then(() => { readyCount += 1; check(); })
        .catch(() => { readyCount += 1; check(); /* count failures too */ });
    });
    const timer = setTimeout(() => { if (!cancelled) setTilesReady(true); }, DECODE_TIMEOUT_MS);
    // Feb-2026 v6.3 (testing agent RCA "/api/gallery/photo is served
    // no-store so warm-cache mount is same speed as cold"): ask the
    // service worker to precache these 16 stable URLs into IMG_CACHE.
    // The Cache API ignores origin Cache-Control, so the NEXT launch
    // will hit cache and mount within ~200 ms regardless of the
    // upstream header.
    try {
      const urls = uniqueTiles.map(fidUrl);
      if (navigator.serviceWorker && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'PRECACHE_MOSAIC_TILES',
          urls,
        });
      }
    } catch { /* SW not available, ignore */ }
    return () => { cancelled = true; clearTimeout(timer); };
    // Stability: only re-run when the FIRST pool arrives (uniqueTiles
    // goes from [] to N). Subsequent pool refreshes keep the mosaic
    // painted without re-gating.
  }, [uniqueTiles.length > 0 ? uniqueTiles.join('|') : '']);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-[#0f1729] overflow-hidden"
      style={{ opacity: fadingOut ? 0 : 1, transition: `opacity ${FADE_MS}ms ease-out` }}
      onClick={() => { setFadingOut(true); setTimeout(() => setVisible(false), FADE_MS); }}
      data-testid="boot-splash"
      data-variant={`v20-${variant.id}`}
    >
      {/* Mosaic — mounts only when tilesReady. Fade class removed
          in v6.3 (testing agent MEDIUM RCA "declared 260 ms fade
          takes 575-700 ms in practice because the animation starts
          after the mount commit + layout of 48-60 background-image
          tiles"). Tiles are already decoded before mount so
          appearing at full opacity is fine. */}
      {tilesReady && (
        <div className={`absolute inset-0 ${variant.anim || ''}`}>
          <div
            className="absolute inset-0 grid"
            style={{
              gridTemplateColumns: `repeat(${cols}, 1fr)`,
              gridAutoRows: `${vp.w / cols}px`,
              gap: `${variant.gap ?? 2}px`,
              padding: `${variant.gap ?? 2}px`,
            }}
            aria-hidden="true"
          >
            {tiles.map((fid, i) => (
              <div
                key={`${fid}-${i}`}
                className="bg-[#141c2e]"
                style={{
                  aspectRatio: '1 / 1',
                  backgroundImage: `url(${fidUrl(fid)})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center 25%',
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty-pool fallback (offline, first-ever boot with no cache
          and API unreachable). Plain dark tiles — no logo backdrop
          per user's "no logo behind squares" rule. This branch fires
          only when the entire pool fetch failed AND the bundled
          snapshot failed. */}
      {tiles.length === 0 && (
        <div
          className="absolute inset-0 grid"
          style={{
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gridAutoRows: `${vp.w / cols}px`,
            gap: `${variant.gap ?? 2}px`,
            padding: `${variant.gap ?? 2}px`,
          }}
          aria-hidden="true"
        >
          {Array.from({ length: cols * rows }).map((_, i) => (
            <div
              key={`skel-${i}`}
              className="bg-[#141c2e]"
              style={{
                aspectRatio: '1 / 1',
                opacity: 0.55 + ((i * 37) % 30) / 100,
              }}
            />
          ))}
        </div>
      )}

      {/* Dim wash (variant-driven). */}
      <div className={`absolute inset-0 ${variant.dim || 'dim-slow'}`} />

      {/* Logo pop (variant-driven). */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <img
          src={LOGO_URL}
          alt="The Vaad Otzar"
          className={`w-[62vmin] max-w-[420px] aspect-square rounded-3xl object-cover ${variant.logo || 'logo-pop'}`}
          style={{ boxShadow: `0 30px 80px ${GOLD}44, 0 0 0 2px ${GOLD}88` }}
        />
      </div>

      {/* Tap-to-skip hint. */}
      <div className="absolute bottom-6 inset-x-0 flex justify-center pointer-events-none">
        <div className="text-white/50 text-[11px] tracking-[0.16em] boot-hint">TAP TO SKIP</div>
      </div>

      <style>{BOOT_KEYFRAMES}</style>
      <style>{`
        @keyframes bootHint { 0%,70% { opacity: 0; } 100% { opacity: 1; } }
        .boot-hint { animation: bootHint ${BOOT_DURATION_MS}ms ease-out forwards; }
      `}</style>
    </div>
  );
};

export default BootSplash;
